import { ProductFlowDataCollection } from '../product-flow/product-flow.types';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	authenticate,
	authenticateAnonymous,
	authorizeAnonymousUserMerge,
	getStateFileStatus,
} from '@app/api/CustomersCanvas';
import { RootReducer, Store } from '@app/app.reducers';
import { ProductEditorData } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import {
	CC_ANONYMOUS_USER_STORAGE_KEY,
	StorePersistService,
} from '@app/checkout/modules/checkout-shared/services/store-persist.service';
import { Direction } from '@app/core/models/step';
import { ConfirmDialogV2Component } from '@app/dialog/components/confirm-dialog-v2/confirm-dialog-v2.component';
import { setStepStateValid } from '@app/product/reducers/product-flow.reducer';
import { hasFetchedPrice } from '@app/product/reducers/product.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import { CustomersCanvasUtil } from '@app/product/utils/customers-canvas';
import { convertMillimetersToPostScriptPoints } from '@app/product/utils/product-utils';
import { SchemaModel } from '@app/schema-form/components/base-schema-form/base-schema-form.component';
import { toFlatObject } from '@app/schema-form/utils/utils';
import { popErrorToast } from '@app/shared/reducers/toast.reducer';
import { NewRelic } from '@app/shared/services/newrelic.service';
import {
	PROOF_IMAGE_OPTIONS,
	PROOF_IMAGE_THUMBNAIL_OPTIONS,
	ProofImageOptions,
} from '@app/shared/services/proof-image.service';
import { StoreDataService } from '@app/shared/services/store-data.service';
import {
	capitalizeFirstLetter,
	defaultsDeep,
	filterTruthy,
	isArrayEqual,
	isEmpty,
	sanitizeFilename,
	timeout,
} from '@app/shared/utils/util';
import { getUser } from '@app/user/reducers/user.reducer';
import type {
	Editor,
	IBarcodeData,
	IConfiguration,
	IProductDefinition,
	ISurfaceDefinition,
} from '@aurigma/design-editor-iframe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment';
import { combineLatest, concat, firstValueFrom, from, of } from 'rxjs';
import { catchError, concatMap, first, map, pluck, share, shareReplay, tap } from 'rxjs/operators';

const EDITOR_LOAD_ERROR_MESSAGE = $localize`:@@EditorLoadError:Failed to load the editor! Try again in a moment or contact support.`;
// A4 size in mm
const DEFAULT_PAGE_DIMENSIONS = {
	width: 210,
	height: 297,
};

/** Product editor component */
@UntilDestroy()
@Component({
	selector: 'g-product-editor',
	templateUrl: './product-editor.component.html',
	styleUrls: ['./product-editor.component.scss'],
	standalone: false,
})
export class ProductEditorComponent implements OnInit, OnDestroy, ProductFlowDataCollection {
	productEditor = inject(ProductEditorService);
	private readonly ccUtil = inject(CustomersCanvasUtil);
	private readonly store = inject<Store<RootReducer.State>>(Store);
	private readonly newRelic = inject(NewRelic);
	private readonly storeDataService = inject(StoreDataService);
	private readonly storePersistService = inject(StorePersistService);
	private readonly dialogService = inject(MatDialog);

	@ViewChild('editor', { static: true }) private readonly editorFrame!: ElementRef<HTMLIFrameElement>;
	isEditorLoaded = false;
	showSpinner = false;
	readonly hasSchemaForm$ = concat(
		of(false),
		this.productEditor.product$.pipe(map((product) => !!product?.metadata?.product_editor_schema_json)),
	).pipe(untilDestroyed(this));

	schemaFormInitialValues?: SchemaModel;
	schemaFormValues?: SchemaModel;
	schemaFormHasErrors = false;

	private readonly apiResolve = this.ccUtil.loadEditorSdk();

	private readonly user$ = this.store.select(getUser).pipe(
		filterTruthy,
		untilDestroyed(this),
		tap((user) => {
			this.setUserInfo(user, this.storeData);
		}),
		shareReplay(1),
	);
	/** @deprecated Use observables directly. */
	get userResolve(): Promise<api.UserDto> {
		return firstValueFrom(this.user$);
	}

	private readonly editor$ = combineLatest([
		this.productEditor.product$.pipe(filterTruthy),
		this.productEditor.selectedTemplates$.pipe(first((t) => Array.isArray(t))),
		this.user$,
	]).pipe(
		untilDestroyed(this),
		concatMap(([product, selectedTemplates]) => {
			return from(this.initEditor(product, selectedTemplates));
		}),
		catchError((error) => {
			this.store.dispatch(popErrorToast({ title: EDITOR_LOAD_ERROR_MESSAGE }));
			this.newRelic.noticeError(error);
			throw error;
		}),
		share(),
	);
	private editor?: Editor;
	get editorResolve(): Promise<Editor> {
		if (this.editor) return Promise.resolve(this.editor);
		return firstValueFrom(this.editor$);
	}

	private readonly window: Window = window;

	private userInfo: SchemaModel = {};
	private readonly storeData = this.storeDataService.storeData;

	ngOnInit(): void {
		this.store.dispatch(setStepStateValid(false));
		this.editor$.subscribe((editor) => {
			this.editor = editor;
		});

		this.productEditor.productEditorChanges$.pipe(first()).subscribe((changes) => {
			if (changes) {
				this.schemaFormInitialValues = changes.schemaForm;
			}
		});
	}

	ngOnDestroy(): void {
		this.productEditor.clearUnsavedWork();
	}

	onSchemaFormError(event: { hasErrors: boolean }): void {
		this.schemaFormHasErrors = event.hasErrors;
		this.updateStepState();
	}

	/** Collects the step data and returns the result. */
	async collectStepData(direction?: Direction): Promise<ProductEditorData | undefined> {
		if (!this.isEditorLoaded) return;
		if (direction === Direction.Previous) return;
		const result = await this.finishDesign();
		// Generate scaled proof images only when advancing to validation step
		const proofImages = await this.getProofImages(PROOF_IMAGE_OPTIONS);
		const proofImageThumbnails = await this.getProofImages(PROOF_IMAGE_THUMBNAIL_OPTIONS);
		const scaledProofImages = (proofImages?.proofImageUrls || []).flat().filter(Boolean);
		const proofImageThumbnail = (proofImageThumbnails?.proofImageUrls || []).flat().find(Boolean);
		this.productEditor.patchProductFlowData({ scaledProofImages, proofImageThumbnail });
		this.productEditor.clearUnsavedWork();
		return { result, scaledProofImages, proofImageThumbnail };
	}

	onSchemaFormChange = async (value: SchemaModel): Promise<void> => {
		// Set initial values if they were not restored from the store.
		if (!this.schemaFormInitialValues)
			this.schemaFormInitialValues = {
				...value,
				...this.userInfo,
			};
		this.schemaFormValues = value;
		// istanbul ignore next - log errors, add better handling if errors are reported.
		void this.editorResolve.then((editor) => {
			return editor.loadUserInfo(toFlatObject(value)).catch((err) => {
				this.newRelic.noticeError(err);
			});
		});
	};

	/** Finish the design and save user changes. */
	async finishDesign(): Promise<Editor.IFinishDesignResult> {
		const extensionPattern = /.*-1_-1\.(\w+)/;
		this.showSpinner = true;
		const result = await this.finishProductDesign();
		const extension = extensionPattern.exec(result.hiResOutputUrls?.[0])?.[1] || 'pdf';
		const userId = result.userId;
		void this.productEditor.uploadPrintfile(result.stateId, userId, false, extension);
		this.productEditor.setPrintfile({
			filename: sanitizeFilename(
				`editor-${this.productEditor.workTitle}-${result.stateId}.${extension}`,
			),
			key: `editor-${result.stateId}.${extension}`,
			editor_state_id: result.stateId,
		});
		this.productEditor.productEditorChanges$.next({
			schemaForm: this.schemaFormValues,
			userChanges: this.getUserChanges(result),
		});
		this.showSpinner = false;
		return result;
	}

	async downloadPDF(): Promise<void> {
		let workTitle = this.productEditor.workTitle;
		if (typeof workTitle !== 'string' || workTitle.length === 0) {
			workTitle = await this.productEditor.getDefaultWorkTitle();
		}
		const timestamp = moment().format('DD-MM-Y HH-mm');
		const fileName = sanitizeFilename(`${workTitle} - ${timestamp}`);
		this.showSpinner = true;
		const result = await this.finishProductDesign(fileName);
		this.showSpinner = false;
		this.window.open(result.hiResOutputUrls[0], '_blank');
	}

	private async finishProductDesign(fileName?: string) {
		try {
			const editor = await this.editorResolve;
			// Hack: Force changes to update before finishing the design.
			await editor.clearSelection().then(() => timeout(500));

			return await this.getValidFinishDesign(fileName);
		} catch (err) {
			this.store.dispatch(
				popErrorToast({
					title: $localize`:@@EditorFinishError:Failed to save the design! Try again in a moment or contact support.`,
				}),
			);
			this.newRelic.noticeError(err);
			throw err;
		}
	}

	// This fixes a problem where state file is not created sometimes
	private async getValidFinishDesign(
		fileName?: string,
		retries = 10,
	): Promise<Editor.IFinishDesignResult> {
		const designParam = fileName ? { fileName } : {};
		const editor = await this.editorResolve;
		const finishResult = await editor.finishProductDesign({
			...designParam,
			pregenerateHiRes: true,
		});

		for (let attempt = retries; attempt--; ) {
			// Check if state file has been created
			const response = await getStateFileStatus(finishResult.userId, finishResult.stateId);
			if (response.data?.isAvailable) return finishResult;
			// Give editor some time.
			await timeout(500);
		}
		throw new Error('Unable to finish product design');
	}

	private async getProofImages(imageOptions: ProofImageOptions) {
		try {
			const editor = await this.editorResolve;
			return await editor.getProofImages(imageOptions);
		} catch (err) {
			this.newRelic.noticeError(err);
			throw err;
		}
	}

	private getUserChanges(result: Editor.IFinishDesignResult): Editor.IUserInfo {
		const changes = result?.userChanges?.inStringPlaceholders || [];
		return Object.fromEntries(changes.map(({ name, usersValue }) => [name, usersValue]));
	}

	private setUserInfo(user: api.UserDto, store: api.StoreDto) {
		const customProps = store?.storefront?.custom_user_schema?.properties || {};
		this.userInfo = {};
		for (const [key, prop] of Object.entries<any>(customProps)) {
			if (prop?.isHiddenFromUser) continue;
			const val = user[key as keyof api.UserDto];
			this.userInfo[capitalizeFirstLetter(key)] = val
				? typeof val === 'object'
					? JSON.stringify(val)
					: `${val}`
				: '';
		}
		this.userInfo = {
			...this.userInfo,
			FirstName: `${user?.firstName || ''}`,
			LastName: `${user?.lastName || ''}`,
			Email: `${user?.email || ''}`,
			Phone: `${user?.phone || ''}`,
		};
	}

	/** Initializes the editor with the given product and selected templates. */
	private async initEditor(product: api.ProductDto, selectedTemplates: string[]) {
		const user = await firstValueFrom(this.user$);
		await this.apiResolve; // TODO: See if we actually need to wait for the cc api to be ready.

		let wasAnon = false;
		let shouldSaveChanges = false;
		// The editor was configured for the anonymous-user, but there's a user now. merge the users and continue.
		const prevEditorUserId = await this.editor?.configuration.getUserId();
		if (this.editor && !user?.isAnonUser && prevEditorUserId?.startsWith('anonymous_')) {
			shouldSaveChanges = await this.shouldSaveChanges();
			// Save changes before merging users
			if (shouldSaveChanges) await this.finishDesign();

			await this.mergeUsers(prevEditorUserId, user.id, this.editor);
			wasAnon = true;
		}

		// Check for changes if user goes back to the editor step so we don't lose any unsaved work.
		const hasChanges = await firstValueFrom(this.productEditor.productEditorChanges$.pipe(map(Boolean)));

		const editor = await this.loadProduct({
			product,
			selectedTemplates,
			stateId: this.productEditor.getPrintfileState(),
			setUserInfo: !shouldSaveChanges && !hasChanges,
		});
		this.productEditor.setUnsavedWork();

		// Trying to reset the anonymous user's editor with an authenticated user's data causes the editor to crash.
		// Calling this doesn't seem to be necessary anyways, and reloading the editor seems to be enough for this use case.
		// Not sure about under what exact circumstances this occurs though.
		if (!wasAnon) await this.setEditorData();

		this.isEditorLoaded = true;
		this.updateStepState();
		if (user?.isAnonUser && !localStorage.getItem(CC_ANONYMOUS_USER_STORAGE_KEY)) {
			this.storePersistService.setStorageKey(
				CC_ANONYMOUS_USER_STORAGE_KEY,
				await editor.configuration.getUserId(),
			);
		}
		return editor;
	}

	/** Asks the user if they want to save changes before continuing. */
	private async shouldSaveChanges(): Promise<boolean> {
		return ConfirmDialogV2Component.open(this.dialogService, {
			title: $localize`:@@ProductEditorKeepUnsavedChanges: Keep unsaved changes?`,
			acceptLabel: $localize`:@@ProductEditorAcceptChanges: Yes`,
			rejectLabel: $localize`:@@ProductEditorRejectChanges: No`,
		});
	}

	/** Merges two users in the editor. */
	private async mergeUsers(sourceUserId: string, destUserId: string, editor: Editor) {
		await authorizeAnonymousUserMerge(sourceUserId);
		await editor.configuration.setUserId(destUserId);
		this.storePersistService.removeStorageKey(CC_ANONYMOUS_USER_STORAGE_KEY);
	}

	/** Updates the step state based on editor loaded and schema form errors. */
	private updateStepState() {
		this.store.dispatch(setStepStateValid(this.isEditorLoaded && !this.schemaFormHasErrors));
	}

	/** Loads the product into the editor. */
	private async loadProduct(options: {
		product: api.ProductDto;
		selectedTemplates: string[];
		stateId: string | undefined;
		setUserInfo?: boolean;
	}): Promise<Editor> {
		const productConfig = this.getEditorMeta(options.product, 'config_json', {}) as IConfiguration;
		const configPreset = (options.product?.metadata?.product_editor_config_preset_data || {}) as {
			editor_configuration?: IConfiguration;
		};
		const editorConfigPreset = configPreset?.editor_configuration || {};
		let config = defaultsDeep(productConfig, editorConfigPreset);

		const auth = await this.authenticateEditor();

		config = {
			...config,
			userInfo: options.setUserInfo
				? (this.userInfo as Record<string, string | IBarcodeData>)
				: undefined,
			...auth,
		};

		const api = await this.apiResolve;
		return await api.loadEditor(
			this.editorFrame.nativeElement,
			await this.generateEditorProduct(options.stateId, options.product, options.selectedTemplates),
			config,
		);
	}

	/**
	 * Authenticates the editor if needed.
	 * @returns The user ID and token ID for the editor, or an empty object if the user is not authenticated,
	 * nor has an anonymous user session.
	 */
	private async authenticateEditor(): Promise<{ userId?: string; tokenId?: string }> {
		const user = await this.userResolve;
		const anonUserId = localStorage.getItem(CC_ANONYMOUS_USER_STORAGE_KEY);

		let authResponse: api.Response<api.TokenResponseDto>;
		if (user?.id) {
			authResponse = await authenticate();
		} else if (anonUserId) {
			authResponse = await authenticateAnonymous({ anonUserId });
		} else {
			return {};
		}

		const tokenId = authResponse?.data?.tokenId;
		const userId = authResponse?.data?.userId;
		// istanbul ignore if - log auth error to New Relic
		if (!tokenId) {
			this.store.dispatch(popErrorToast({ title: EDITOR_LOAD_ERROR_MESSAGE }));
			const errMsg = 'Failed to authenticate editor!';
			this.newRelic.noticeError(new Error(errMsg), { userId: user?.id, anonUserId });
			throw new Error(errMsg);
		}
		return { userId, tokenId };
	}

	/**
	 * @returns A product state ID or product configuration
	 */
	private async generateEditorProduct(
		stateId: string | undefined,
		product: api.ProductDto,
		selectedTemplates: string[],
	): Promise<string | IProductDefinition> {
		const cartItem = this.productEditor.cartItem;
		if (cartItem) {
			// When editing cart item and selected templates haven't changed, use state ID
			const cartItemTemplates = cartItem?.options?.selectedTemplates || [];
			if (stateId && isArrayEqual(cartItemTemplates, selectedTemplates)) return stateId;
		} else if (stateId) return stateId;

		// Otherwise generate product configuration based on product data and selected templates
		this.productEditor.productEditorChanges$.next(undefined);
		return this.configureProduct(product, selectedTemplates);
	}

	private async configureProduct(
		product: api.ProductDto,
		selectedTemplates: string[],
	): Promise<IProductDefinition> {
		const surfaces: string[] = this.getEditorMeta(product, 'template', '').split(',').filter(Boolean);
		const productDefinition: IProductDefinition = this.getEditorMeta(
			product,
			'product_definition_json',
			{},
		);
		const configPreset = (product?.metadata?.product_editor_config_preset_data || {}) as {
			product_definition: IProductDefinition;
		};
		const productDefinitionPreset = configPreset?.product_definition;
		const definition = defaultsDeep(productDefinition, productDefinitionPreset, { surfaces });
		if (this.validateSelectedTemplates(selectedTemplates)) {
			// User selected templates are preferred over all others
			definition.surfaces = selectedTemplates;
		} else if (isEmpty(definition.surfaces)) {
			// No print surfaces have been defined, create a blank surfaces based on product size/scope.
			definition.surfaces = await this.buildPagesFromProductOptions();
		}
		return definition;
	}

	private validateSelectedTemplates(selectedTemplates: string[]): boolean {
		return selectedTemplates?.length > 0 && selectedTemplates.every((template) => template?.length > 0);
	}

	private async buildPagesFromProductOptions(): Promise<ISurfaceDefinition[]> {
		const options = await firstValueFrom(this.productEditor.selectedOptions$);
		const pageCount = options?.scope || 1;
		const size = options?.size;
		let width: number, height: number;
		if (isEmpty(size)) {
			// Determine dimensions based on selected width/height options or use defaults
			width = options?.width || DEFAULT_PAGE_DIMENSIONS.width;
			height = options?.height || DEFAULT_PAGE_DIMENSIONS.height;
		} else {
			// Determine dimensions based on selected size, dimensions weirdly found in price data
			const price = await this.productEditor.price$
				.pipe(first(hasFetchedPrice), pluck('price'))
				.toPromise();
			width = price?.size?.width || DEFAULT_PAGE_DIMENSIONS.width;
			height = price?.size?.height || DEFAULT_PAGE_DIMENSIONS.height;
		}

		width = convertMillimetersToPostScriptPoints(width);
		height = convertMillimetersToPostScriptPoints(height);

		return Array.from({ length: pageCount }, (_, i) => ({
			width,
			height,
			name: this.buildPageName(i + 1, pageCount),
		}));
	}

	private buildPageName(pageNumber: number, totalPages: number): string {
		if (pageNumber === 1) {
			return $localize`:@@EditorFrontPage:Front`;
		}
		if (pageNumber === totalPages) {
			return $localize`:@@EditorBackPage:Back`;
		}
		return `${$localize`:@@EditorPage:Page`} ${pageNumber}`;
	}

	private async setEditorData() {
		if (!this.isEditorLoaded) return;
		const editor = await this.editorResolve;
		const changes = await firstValueFrom(this.productEditor.productEditorChanges$);
		// User has made changes earlier, do not revert the product.
		if (changes && !isEmpty(changes.userChanges)) {
			await editor.loadUserInfo(changes.userChanges);
			return;
		}

		// New product, so reset the editor and load user data.
		await editor.revertProduct();

		const info = {
			...this.userInfo,
			...toFlatObject(this.schemaFormInitialValues || {}),
		};
		if (!isEmpty(info)) {
			await editor.loadUserInfo(info);
		}
	}

	private getEditorMeta<
		T extends Extract<keyof api.ProductDto['metadata'], `product_editor_${string}`>,
		K extends T extends `product_editor_${infer R}` ? R : string,
		TDefault,
	>(product: api.ProductDto, key: K, defaultValue?: TDefault): TDefault | api.ProductDto['metadata'][T] {
		return product?.metadata?.[`product_editor_${key}`] || defaultValue;
	}
}
